import './profilePage.css';
import React, {Component} from "react";
import UserDetailsService from "../services/userDetailsService";
import {Navigate} from "react-router-dom";
import RedirectionService from "../services/redirectionService";
import {UserDto, BasicUserDto} from "../model/functionModels";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import UserDisplay from "./userDisplay";
import FriendService from "../services/friendService";
import LocalStorageService from "../services/localStorageService";
import PaymentMethodService from "../services/paymentMethodService";
import {PaymentMethodDto, PaymentMethodType, GetPaymentMethodsResponseDto} from "../model/functionModels";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface ProfileState {
    navigation?: string,
    loggedInUser?: UserDto,
    allUsers?: (UserDto | BasicUserDto)[],
    newUsername: string,
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    showCurrentPassword: boolean,
    showNewPassword: boolean,
    showConfirmPassword: boolean,
    iconUrl: string,
    useGravatar: boolean,
    usernameError?: string,
    passwordError?: string,
    iconError?: string,
    deleteAccountConfirmText?: string,
    isLoading: boolean,
    paymentMethods?: PaymentMethodDto[],
    showAddPaymentMethod: boolean,
    newPaymentMethodType?: PaymentMethodType,
    newPaymentMethodConfig: string,
    newPaymentMethodPriority: number,
    paymentMethodError?: string,
    enabledPaymentMethodsForPaying: PaymentMethodType[],
    draggedPaymentMethodId?: string,
    touchStartY?: number,
    touchStartMethodId?: string,
}

interface ProfileProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
    onLogout: any,
}

class ProfilePage extends Component<ProfileProps, ProfileState> {
    private unsubscribe?: () => void;
    private paymentMethodRefs: Map<string, HTMLDivElement> = new Map();

    constructor(props: ProfileProps) {
        super(props, {})
        this.state = {
            newUsername: "",
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
            showCurrentPassword: false,
            showNewPassword: false,
            showConfirmPassword: false,
            iconUrl: "",
            useGravatar: true,
            isLoading: false,
            showAddPaymentMethod: false,
            newPaymentMethodConfig: "",
            newPaymentMethodPriority: 0,
            enabledPaymentMethodsForPaying: [],
        };
        this.handleFailure = this.handleFailure.bind(this);
        this.handleRedirection = this.handleRedirection.bind(this);
        this.refreshAll = this.refreshAll.bind(this);
        this.handleUsernameUpdate = this.handleUsernameUpdate.bind(this);
        this.handleChangePassword = this.handleChangePassword.bind(this);
        this.handleIconUpdate = this.handleIconUpdate.bind(this);
        this.handleDeleteAccount = this.handleDeleteAccount.bind(this);
        this.handleExportMyData = this.handleExportMyData.bind(this);
        this.toggleCurrentPasswordVisibility = this.toggleCurrentPasswordVisibility.bind(this);
        this.toggleNewPasswordVisibility = this.toggleNewPasswordVisibility.bind(this);
        this.toggleConfirmPasswordVisibility = this.toggleConfirmPasswordVisibility.bind(this);
        this.handleAddPaymentMethod = this.handleAddPaymentMethod.bind(this);
        this.handleDeletePaymentMethod = this.handleDeletePaymentMethod.bind(this);
        this.handleUpdatePaymentMethodPriority = this.handleUpdatePaymentMethodPriority.bind(this);
        this.loadPaymentMethods = this.loadPaymentMethods.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);
    }

    async componentDidMount() {
        // Subscribe to locale changes to trigger re-render
        this.unsubscribe = i18nService.subscribe(() => {
            this.forceUpdate();
        });
        
        if (!this.state.navigation) {
            await this.refreshAll();
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        // Clean up touch event listeners
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleTouchEnd);
        document.removeEventListener('touchcancel', this.handleTouchEnd);
        // Clean up refs
        this.paymentMethodRefs.forEach((el) => {
            const handler = (el as any).__touchStartHandler;
            if (handler) {
                el.removeEventListener('touchstart', handler);
            }
        });
        this.paymentMethodRefs.clear();
    }

    async refreshAll() {
        // Parallelize API calls for better performance
        const promises = [
            UserDetailsService.getOwnUserDetails(async userDto => {
                const useGravatar = !userDto.icon || (!!userDto.gravatarIcon && userDto.gravatarIcon.length > 0);
                this.setState({
                    loggedInUser: userDto,
                    newUsername: userDto.username,
                    usernameError: undefined,
                    iconUrl: userDto.icon || "",
                    useGravatar: useGravatar,
                    iconError: undefined,
                });
            }, async () => {
                // @ts-ignore
                const currentPath = this.props.location?.pathname || '/profile';
                this.setState({
                    navigation: RedirectionService.redirectToLogin(currentPath),
                })
            },
            this.handleRedirection),
            UserDetailsService.getAllUsersList(async users => {
                this.setState({
                    allUsers: users,
                })
            }, this.handleFailure, this.handleRedirection),
            this.loadPaymentMethods()
        ];
        
        // Wait for all API calls to complete in parallel
        await Promise.all(promises);
    }

    async loadPaymentMethods() {
        await PaymentMethodService.getPaymentMethods(
            async (response: GetPaymentMethodsResponseDto) => {
                this.setState({
                    paymentMethods: response.methods,
                    enabledPaymentMethodsForPaying: response.methods
                        .filter(m => m.enabledForPaying)
                        .map(m => m.methodType),
                });
            },
            this.handleFailure,
            this.handleRedirection
        );
    }

    async handleRedirection(redirectionUrl: string) {
        if (redirectionUrl.startsWith("/login")) {
            this.props.onLogout();
        }
        this.setState({
            navigation: redirectionUrl
        });
    }

    async handleFailure(error: string) {
        this.props.toastGenerator(error, ToastErrorLevel.ERROR);
    }

    async handleUsernameUpdate() {
        if (!this.state.newUsername.trim() || this.state.newUsername.trim().length < 3) {
            this.setState({usernameError: "Username must be at least 3 characters"});
            return;
        }
        if (this.state.newUsername.trim() === this.state.loggedInUser?.username) {
            this.setState({usernameError: undefined});
            return;
        }
        this.setState({isLoading: true, usernameError: undefined});
        await FriendService.updateUsername({
            username: this.state.newUsername.trim(),
        }, async () => {
            this.props.toastGenerator("Username updated successfully!", ToastErrorLevel.INFO);
            this.setState({usernameError: undefined});
            await this.refreshAll();
        }, async (error: string) => {
            if (error.includes("already taken") || error.includes("409")) {
                this.setState({usernameError: "This username is already taken. Please choose another."});
            } else {
                this.setState({usernameError: error});
            }
            this.props.toastGenerator(error, ToastErrorLevel.ERROR);
        }, this.handleRedirection);
        this.setState({isLoading: false});
    }

    async handleIconUpdate() {
        if (!this.state.loggedInUser) {
            return;
        }
        if (!this.state.useGravatar && (!this.state.iconUrl || this.state.iconUrl.trim().length === 0)) {
            this.setState({iconError: "Icon URL is required when gravatar is disabled"});
            return;
        }

        const currentUseGravatar = !this.state.loggedInUser.icon || (this.state.loggedInUser.gravatarIcon && this.state.loggedInUser.gravatarIcon.length > 0);
        const currentIconUrl = this.state.loggedInUser.icon || "";
        if (this.state.useGravatar === currentUseGravatar && this.state.iconUrl === currentIconUrl) {
            return;
        }

        this.setState({isLoading: true, iconError: undefined});
        await FriendService.updateIcon({
            icon: this.state.useGravatar ? undefined : this.state.iconUrl.trim(),
            gravatarEnabled: this.state.useGravatar,
        }, async () => {
            this.props.toastGenerator("Icon updated successfully!", ToastErrorLevel.INFO);
            await this.refreshAll();
        }, async (error: string) => {
            this.setState({iconError: error});
            this.props.toastGenerator(error, ToastErrorLevel.ERROR);
        }, this.handleRedirection);
        this.setState({isLoading: false});
    }

    async handleChangePassword() {
        if (!this.state.currentPassword || !this.state.newPassword || !this.state.confirmPassword) {
            this.setState({passwordError: "All password fields are required"});
            return;
        }
        if (this.state.newPassword.length < 6) {
            this.setState({passwordError: "New password must be at least 6 characters"});
            return;
        }
        if (this.state.newPassword !== this.state.confirmPassword) {
            this.setState({passwordError: "New passwords do not match"});
            return;
        }
        this.setState({isLoading: true, passwordError: undefined});
        await FriendService.changePassword({
            currentPassword: this.state.currentPassword,
            newPassword: this.state.newPassword,
        }, async () => {
            this.props.toastGenerator("Password changed successfully!", ToastErrorLevel.INFO);
            this.setState({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
                passwordError: undefined,
            });
        }, async (error: string) => {
            this.setState({passwordError: error});
            this.props.toastGenerator(error, ToastErrorLevel.ERROR);
        }, this.handleRedirection);
        this.setState({isLoading: false});
    }

    toggleCurrentPasswordVisibility() {
        this.setState({showCurrentPassword: !this.state.showCurrentPassword});
    }

    toggleNewPasswordVisibility() {
        this.setState({showNewPassword: !this.state.showNewPassword});
    }

    toggleConfirmPasswordVisibility() {
        this.setState({showConfirmPassword: !this.state.showConfirmPassword});
    }

    async handleDeleteAccount() {
        const typed = window.prompt("Type DELETE to permanently delete your account.");
        if (typed !== "DELETE") {
            return;
        }
        if (!window.confirm("Final confirmation: this will remove your friend connections and you will be signed out.")) {
            return;
        }
        this.setState({isLoading: true});
        await FriendService.deleteMyAccount(
            async () => {
                this.props.toastGenerator("Account deleted.", ToastErrorLevel.INFO);
                LocalStorageService.signOut();
                this.props.onLogout();
                this.setState({navigation: RedirectionService.redirectToLogin("/login")});
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    async handleAddPaymentMethod() {
        if (!this.state.newPaymentMethodType || !this.state.newPaymentMethodConfig.trim()) {
            this.setState({paymentMethodError: "Please select a method type and provide configuration"});
            return;
        }
        if (this.state.newPaymentMethodPriority < 0) {
            this.setState({paymentMethodError: "Priority must be 0 or greater"});
            return;
        }

        this.setState({isLoading: true, paymentMethodError: undefined});
        await PaymentMethodService.addPaymentMethod({
            methodType: this.state.newPaymentMethodType,
            priority: this.state.newPaymentMethodPriority,
            configData: this.state.newPaymentMethodConfig.trim(),
        }, async () => {
            this.props.toastGenerator("Payment method added!", ToastErrorLevel.INFO);
            this.setState({
                showAddPaymentMethod: false,
                newPaymentMethodType: undefined,
                newPaymentMethodConfig: "",
                newPaymentMethodPriority: 0,
            });
            await this.loadPaymentMethods();
        }, async (error: string) => {
            this.setState({paymentMethodError: error});
            this.props.toastGenerator(error, ToastErrorLevel.ERROR);
        }, this.handleRedirection);
        this.setState({isLoading: false});
    }

    async handleDeletePaymentMethod(paymentMethodId: string) {
        if (!window.confirm("Delete this payment method?")) {
            return;
        }
        this.setState({isLoading: true});
        await PaymentMethodService.deletePaymentMethod(
            paymentMethodId,
            async () => {
                this.props.toastGenerator("Payment method deleted.", ToastErrorLevel.INFO);
                await this.loadPaymentMethods();
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    async handleUpdatePaymentMethodPriority(paymentMethodId: string, newPriority: number) {
        this.setState({isLoading: true});
        await PaymentMethodService.updatePaymentMethodPriority({
            paymentMethodId,
            priority: newPriority,
        }, async () => {
            this.props.toastGenerator("Priority updated!", ToastErrorLevel.INFO);
            await this.loadPaymentMethods();
        }, this.handleFailure, this.handleRedirection);
        this.setState({isLoading: false});
    }

    async handleUpdatePaymentMethodsForPaying(enabledMethodTypes: PaymentMethodType[]) {
        this.setState({isLoading: true});
        await PaymentMethodService.updatePaymentMethodsForPaying({
            enabledMethodTypes: enabledMethodTypes,
        }, async () => {
            this.props.toastGenerator(i18nService.t('profile.paymentMethodsForPayingUpdated'), ToastErrorLevel.INFO);
            await this.loadPaymentMethods();
        }, this.handleFailure, this.handleRedirection);
        this.setState({isLoading: false});
    }

    handleDragStart(e: React.DragEvent, methodId: string) {
        this.setState({draggedPaymentMethodId: methodId});
        e.dataTransfer.effectAllowed = "move";
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "0.5";
        }
    }

    handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }

    async handleDrop(e: React.DragEvent, targetMethodId: string) {
        e.preventDefault();
        const draggedId = this.state.draggedPaymentMethodId;
        if (!draggedId || draggedId === targetMethodId || !this.state.paymentMethods) {
            this.setState({draggedPaymentMethodId: undefined});
            return;
        }

        const methods = [...this.state.paymentMethods].sort((a, b) => a.priority - b.priority);
        const draggedIndex = methods.findIndex(m => m.id === draggedId);
        const targetIndex = methods.findIndex(m => m.id === targetMethodId);

        if (draggedIndex === -1 || targetIndex === -1) {
            this.setState({draggedPaymentMethodId: undefined});
            return;
        }

        // Reorder array
        const [removed] = methods.splice(draggedIndex, 1);
        methods.splice(targetIndex, 0, removed);

        // Update priorities
        const newOrder = methods.map(m => m.id);
        this.setState({isLoading: true});
        await PaymentMethodService.reorderPaymentMethods(
            { orderedPaymentMethodIds: newOrder },
            async () => {
                await this.loadPaymentMethods();
                this.props.toastGenerator("Payment methods reordered!", ToastErrorLevel.INFO);
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false, draggedPaymentMethodId: undefined});
    }

    handleDragEnd(e: React.DragEvent) {
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "1";
        }
        this.setState({draggedPaymentMethodId: undefined});
    }

    handleTouchStart = (e: TouchEvent, methodId: string) => {
        const touch = e.touches[0];
        this.setState({
            touchStartY: touch.clientY,
            touchStartMethodId: methodId,
            draggedPaymentMethodId: methodId,
        });
        const element = e.currentTarget as HTMLElement;
        if (element) {
            element.style.opacity = "0.5";
        }
        // Add global touch move and end listeners
        document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        document.addEventListener('touchend', this.handleTouchEnd, { passive: false });
        document.addEventListener('touchcancel', this.handleTouchEnd, { passive: false });
    }

    handleTouchMove = (e: TouchEvent) => {
        if (!this.state.touchStartY || !this.state.touchStartMethodId || !this.state.paymentMethods) {
            return;
        }

        e.preventDefault(); // Prevent scrolling while dragging
        
        const touch = e.touches[0];
        const currentY = touch.clientY;
        
        // Find which element the touch is currently over
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        const targetItem = elementBelow?.closest('[data-method-id]') as HTMLElement;
        
        if (targetItem && targetItem.dataset.methodId && targetItem.dataset.methodId !== this.state.touchStartMethodId) {
            // Visual feedback: move the dragged item
            const draggedElement = document.querySelector(`[data-method-id="${this.state.touchStartMethodId}"]`) as HTMLElement;
            if (draggedElement) {
                const offset = currentY - (this.state.touchStartY || 0);
                draggedElement.style.transform = `translateY(${offset}px)`;
            }
        }
    }

    handleTouchEnd = async (e: TouchEvent) => {
        // Remove global listeners
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleTouchEnd);
        document.removeEventListener('touchcancel', this.handleTouchEnd);
        
        const draggedElement = document.querySelector(`[data-method-id="${this.state.touchStartMethodId}"]`) as HTMLElement;
        
        // Reset transforms
        document.querySelectorAll('[data-method-id]').forEach(el => {
            if (el instanceof HTMLElement) {
                el.style.transform = '';
            }
        });

        if (draggedElement) {
            draggedElement.style.opacity = "1";
        }

        if (!this.state.touchStartMethodId || !this.state.paymentMethods) {
            this.setState({
                touchStartY: undefined,
                touchStartMethodId: undefined,
                draggedPaymentMethodId: undefined,
            });
            return;
        }

        const touch = e.changedTouches[0];
        
        // Find which element the touch ended over
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        const targetItem = elementBelow?.closest('[data-method-id]') as HTMLElement;
        const targetMethodId = targetItem?.dataset.methodId;

        if (!targetMethodId || targetMethodId === this.state.touchStartMethodId) {
            this.setState({
                touchStartY: undefined,
                touchStartMethodId: undefined,
                draggedPaymentMethodId: undefined,
            });
            return;
        }

        const methods = [...this.state.paymentMethods].sort((a, b) => a.priority - b.priority);
        const startIndex = methods.findIndex(m => m.id === this.state.touchStartMethodId);
        const targetIndex = methods.findIndex(m => m.id === targetMethodId);
        
        if (startIndex === -1 || targetIndex === -1) {
            this.setState({
                touchStartY: undefined,
                touchStartMethodId: undefined,
                draggedPaymentMethodId: undefined,
            });
            return;
        }

        if (startIndex !== targetIndex) {
            // Reorder array
            const [removed] = methods.splice(startIndex, 1);
            methods.splice(targetIndex, 0, removed);

            // Update priorities
            const newOrder = methods.map(m => m.id);
            this.setState({isLoading: true});
            await PaymentMethodService.reorderPaymentMethods(
                { orderedPaymentMethodIds: newOrder },
                async () => {
                    await this.loadPaymentMethods();
                    this.props.toastGenerator(i18nService.t('profile.paymentMethodsReordered'), ToastErrorLevel.INFO);
                },
                this.handleFailure,
                this.handleRedirection
            );
            this.setState({isLoading: false});
        }

        this.setState({
            touchStartY: undefined,
            touchStartMethodId: undefined,
            draggedPaymentMethodId: undefined,
        });
    }

    async handleExportMyData() {
        this.setState({isLoading: true});
        await FriendService.exportMyData(
            async (data) => {
                const json = JSON.stringify(data, null, 2);
                const blob = new Blob([json], {type: "application/json"});
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                const date = new Date().toISOString().slice(0, 10);
                a.href = url;
                a.download = `expense-tracker-data-${date}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                this.props.toastGenerator("Downloaded your data export.", ToastErrorLevel.INFO);
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>
        }
        if (!this.state.loggedInUser || !this.state.allUsers) {
            return <div className="ProfilePage-Loading">
                <LoadingSpinner size="large" message={i18nService.t('common.loading')} />
            </div>
        }
        const currentUseGravatar = !this.state.loggedInUser.icon || (this.state.loggedInUser.gravatarIcon && this.state.loggedInUser.gravatarIcon.length > 0);

        return <div className="ProfilePage-Wrapper">
            <div className="ProfilePage-Container">
                <h1 className="ProfilePage-Title">{i18nService.t('profile.title')}</h1>
                
                <div className="ProfilePage-Section">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.username')}</h2>
                    <p className="ProfilePage-SectionDescription">{i18nService.t('profile.changeUsername')}</p>
                    <div className="ProfilePage-InputGroup">
                        <input 
                            type="text" 
                            className="ProfilePage-Input"
                            value={this.state.newUsername} 
                            onChange={e => this.setState({newUsername: e.target.value, usernameError: undefined})}
                            placeholder={i18nService.t('auth.username')}
                            disabled={this.state.isLoading}
                        />
                        <button 
                            className="ProfilePage-Button"
                            onClick={this.handleUsernameUpdate}
                            disabled={this.state.isLoading || this.state.newUsername.trim() === this.state.loggedInUser.username}
                        >
                            {this.state.isLoading ? i18nService.t('common.loading') : i18nService.t('profile.updateUsername')}
                        </button>
                    </div>
                    {this.state.usernameError && (
                        <div className="ProfilePage-Error">{this.state.usernameError}</div>
                    )}
                    {this.state.loggedInUser.username && (
                        <div className="ProfilePage-Info">
                            <strong>{i18nService.t('profile.currentUsername')}:</strong> {this.state.loggedInUser.username}
                        </div>
                    )}
                </div>

                <div className="ProfilePage-Section">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.icon')}</h2>
                    <p className="ProfilePage-SectionDescription">{i18nService.t('profile.iconDescription')}</p>
                    <div className="ProfilePage-IconEditor">
                        <div className="ProfilePage-AvatarPreviewCard">
                            <div className="ProfilePage-AvatarPreviewLabel">Preview</div>
                            <div
                                className="ProfilePage-AvatarPreview"
                                style={{
                                    backgroundImage: `url(${this.state.useGravatar
                                        ? (this.state.loggedInUser.gravatarIcon || this.state.loggedInUser.icon || "")
                                        : (this.state.iconUrl || "")
                                    })`,
                                }}
                            />
                        </div>
                        <div className="ProfilePage-IconControls">
                            <div className="ProfilePage-ToggleRow">
                                <div className="ProfilePage-ToggleText">
                                    <div className="ProfilePage-ToggleLabel">{i18nService.t('profile.useGravatar')}</div>
                                    <div className="ProfilePage-ToggleSubLabel">{i18nService.t('profile.gravatarDescription')}</div>
                                </div>
                                <label className="ProfilePage-ToggleSwitch" aria-label={i18nService.t('profile.useGravatar')}>
                                    <input
                                        type="checkbox"
                                        checked={this.state.useGravatar}
                                        onChange={(e) => {
                                            this.setState({
                                                useGravatar: e.target.checked,
                                                iconError: undefined,
                                            });
                                        }}
                                        disabled={this.state.isLoading}
                                    />
                                    <span className="ProfilePage-ToggleSlider"></span>
                                </label>
                            </div>
                            <div className={`ProfilePage-IconUrlRow ${this.state.useGravatar ? "ProfilePage-IconUrlRowHidden" : ""}`}>
                                <input 
                                    type="text"
                                    className="ProfilePage-Input"
                                    value={this.state.useGravatar ? "" : this.state.iconUrl}
                                    onChange={e => this.setState({iconUrl: e.target.value, iconError: undefined})}
                                    placeholder={this.state.useGravatar ? i18nService.t('profile.usingGravatar') : i18nService.t('profile.iconUrl')}
                                    disabled={this.state.isLoading || this.state.useGravatar}
                                />
                            </div>
                            {this.state.iconError && (
                                <div className="ProfilePage-Error">{this.state.iconError}</div>
                            )}
                        </div>
                    </div>
                    <div className="ProfilePage-ButtonRow ProfilePage-ButtonRowFullWidth">
                        <button 
                            className="ProfilePage-Button"
                            onClick={this.handleIconUpdate}
                            disabled={this.state.isLoading || (this.state.useGravatar === currentUseGravatar && this.state.iconUrl === (this.state.loggedInUser.icon || ""))}
                        >
                            {this.state.isLoading ? i18nService.t('common.loading') : i18nService.t('profile.updateIcon')}
                        </button>
                    </div>
                </div>

                <div className="ProfilePage-Section">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.paymentMethods')}</h2>
                    <p className="ProfilePage-SectionDescription">
                        {i18nService.t('profile.paymentMethodsDescription')}
                    </p>
                    
                    <h3 className="ProfilePage-SubSectionTitle">{i18nService.t('profile.methodsForReceiving')}</h3>
                    <p className="ProfilePage-SectionDescription" style={{fontSize: "0.9em", marginBottom: "var(--spacing-md)"}}>
                        {i18nService.t('profile.reorderHint')}
                    </p>
                    
                    <div className="ProfilePage-PaymentMethodsList">
                        {this.state.paymentMethods && this.state.paymentMethods.length > 0 ? (
                            this.state.paymentMethods
                                .sort((a, b) => a.priority - b.priority)
                                .map((method, index) => (
                                    <div 
                                        key={method.id}
                                        ref={(el) => {
                                            if (el) {
                                                const oldEl = this.paymentMethodRefs.get(method.id);
                                                if (oldEl && oldEl !== el) {
                                                    const handler = (oldEl as any).__touchStartHandler;
                                                    if (handler) {
                                                        oldEl.removeEventListener('touchstart', handler);
                                                    }
                                                }
                                                this.paymentMethodRefs.set(method.id, el);
                                                // Add touch start listener with passive: false
                                                const touchStartHandler = (e: TouchEvent) => this.handleTouchStart(e, method.id);
                                                (el as any).__touchStartHandler = touchStartHandler;
                                                el.addEventListener('touchstart', touchStartHandler, { passive: false });
                                            } else {
                                                const oldEl = this.paymentMethodRefs.get(method.id);
                                                if (oldEl) {
                                                    const handler = (oldEl as any).__touchStartHandler;
                                                    if (handler) {
                                                        oldEl.removeEventListener('touchstart', handler);
                                                    }
                                                }
                                                this.paymentMethodRefs.delete(method.id);
                                            }
                                        }}
                                        data-method-id={method.id}
                                        className={`ProfilePage-PaymentMethodItem ${this.state.draggedPaymentMethodId === method.id ? 'ProfilePage-PaymentMethodItemDragging' : ''}`}
                                        draggable={!this.state.isLoading}
                                        onDragStart={(e) => this.handleDragStart(e, method.id)}
                                        onDragOver={this.handleDragOver}
                                        onDrop={(e) => this.handleDrop(e, method.id)}
                                        onDragEnd={this.handleDragEnd}
                                    >
                                        <div className="ProfilePage-PaymentMethodDragHandle" title="Drag to reorder">
                                            ⋮⋮
                                        </div>
                                        <div className="ProfilePage-PaymentMethodInfo">
                                            <div className="ProfilePage-PaymentMethodName">
                                                <strong>{method.methodType === "REVOLUT_ME" ? i18nService.t('profile.revolutMe') : i18nService.t('profile.paypalMe')}</strong>
                                                {method.enabledForReceiving && method.configData && (
                                                    <span className="ProfilePage-PaymentMethodLink">
                                                        {method.methodType === "REVOLUT_ME" ? "https://revolut.me/" : "https://paypal.me/"}{method.configData}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ProfilePage-PaymentMethodStatus">
                                                        {method.enabledForReceiving && <span className="ProfilePage-PaymentMethodBadge ProfilePage-PaymentMethodBadgeReceiving">{i18nService.t('profile.receiving')}</span>}
                                                        {method.enabledForPaying && <span className="ProfilePage-PaymentMethodBadge ProfilePage-PaymentMethodBadgePaying">{i18nService.t('profile.paying')}</span>}
                                            </div>
                                            <div className="ProfilePage-PaymentMethodOrder">
                                                    {i18nService.t('profile.priority')}: #{index + 1}
                                            </div>
                                        </div>
                                        <button
                                            className="ProfilePage-PaymentMethodDelete"
                                            onClick={() => this.handleDeletePaymentMethod(method.id)}
                                            disabled={this.state.isLoading}
                                            title="Delete payment method"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                ))
                        ) : (
                            <p className="ProfilePage-EmptyState">{i18nService.t('profile.noPaymentMethods')}</p>
                        )}
                    </div>

                    {(() => {
                        // Get available payment method types (filter out already configured non-multiple methods)
                        const configuredMethodTypes = (this.state.paymentMethods || [])
                            .filter(m => m.enabledForReceiving && !m.allowsMultiple)
                            .map(m => m.methodType);
                        const availableMethods = (["REVOLUT_ME", "PAYPAL_ME"] as PaymentMethodType[]).filter(m => !configuredMethodTypes.includes(m));
                        
                        if (availableMethods.length === 0) {
                            return null; // Hide button if no methods available
                        }
                        
                        return !this.state.showAddPaymentMethod ? (
                            <button
                                className="ProfilePage-Button"
                                onClick={() => this.setState({showAddPaymentMethod: true, paymentMethodError: undefined})}
                                disabled={this.state.isLoading}
                            >
                                {i18nService.t('profile.addPaymentMethod')}
                            </button>
                        ) : (
                            <div className="ProfilePage-AddPaymentMethodForm">
                                <h3 className="ProfilePage-SubSectionTitle">{i18nService.t('profile.addPaymentMethodTitle')}</h3>
                                <div className="ProfilePage-InputGroup">
                                    <label className="ProfilePage-Label">{i18nService.t('profile.methodType')}</label>
                                    <select
                                        className="ProfilePage-Input"
                                        value={this.state.newPaymentMethodType || ""}
                                        onChange={(e) => this.setState({
                                            newPaymentMethodType: e.target.value as PaymentMethodType,
                                            newPaymentMethodConfig: "", // Clear config when method changes
                                            paymentMethodError: undefined,
                                        })}
                                        disabled={this.state.isLoading}
                                    >
                                        <option value="">{i18nService.t('profile.selectMethod')}</option>
                                        {availableMethods.map(method => (
                                            <option key={method} value={method}>
                                                {method === "REVOLUT_ME" ? i18nService.t('profile.revolutMe') : i18nService.t('profile.paypalMe')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {this.state.newPaymentMethodType && (
                                    <div className="ProfilePage-InputGroup">
                                        <label className="ProfilePage-Label">
                                            {this.state.newPaymentMethodType === "REVOLUT_ME" ? i18nService.t('profile.revolutId') : i18nService.t('profile.paypalUsername')}
                                        </label>
                                        <input
                                            type="text"
                                            className="ProfilePage-Input"
                                            value={this.state.newPaymentMethodConfig}
                                            onChange={(e) => this.setState({
                                                newPaymentMethodConfig: e.target.value,
                                                paymentMethodError: undefined,
                                            })}
                                            placeholder={this.state.newPaymentMethodType === "REVOLUT_ME" ? i18nService.t('profile.yourRevolutId') : i18nService.t('profile.yourPaypalUsername')}
                                            disabled={this.state.isLoading}
                                        />
                                    </div>
                                )}
                                <div className="ProfilePage-InputGroup">
                                    <label className="ProfilePage-Label">{i18nService.t('profile.priorityLabel')}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="ProfilePage-Input"
                                        value={this.state.newPaymentMethodPriority}
                                        onChange={(e) => this.setState({
                                            newPaymentMethodPriority: parseInt(e.target.value) || 0,
                                            paymentMethodError: undefined,
                                        })}
                                        disabled={this.state.isLoading}
                                    />
                                </div>
                                {this.state.paymentMethodError && (
                                    <div className="ProfilePage-Error">{this.state.paymentMethodError}</div>
                                )}
                                <div className="ProfilePage-ButtonRow">
                                    <button
                                        className="ProfilePage-Button"
                                        onClick={this.handleAddPaymentMethod}
                                        disabled={this.state.isLoading || !this.state.newPaymentMethodType || !this.state.newPaymentMethodConfig.trim()}
                                    >
                                        {this.state.isLoading ? i18nService.t('profile.adding') : i18nService.t('profile.add')}
                                    </button>
                                    <button
                                        className="ProfilePage-Button ProfilePage-ButtonSecondary"
                                        onClick={() => this.setState({
                                            showAddPaymentMethod: false,
                                            newPaymentMethodType: undefined,
                                            newPaymentMethodConfig: "",
                                            newPaymentMethodPriority: 0,
                                            paymentMethodError: undefined,
                                        })}
                                        disabled={this.state.isLoading}
                                    >
                                        {i18nService.t('common.cancel')}
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    <h3 className="ProfilePage-SubSectionTitle" style={{marginTop: "var(--spacing-xl)"}}>{i18nService.t('profile.methodsForPaying')}</h3>
                    <p className="ProfilePage-SectionDescription" style={{fontSize: "0.9em", marginBottom: "var(--spacing-md)"}}>
                        {i18nService.t('profile.payingDescription')}
                    </p>
                        <div className="ProfilePage-PaymentMethodsForPaying">
                            <div className="ProfilePage-ToggleRow">
                                <div className="ProfilePage-ToggleText">
                                    <div className="ProfilePage-ToggleLabel">{i18nService.t('profile.revolutMe')}</div>
                                </div>
                                <label className="ProfilePage-ToggleSwitch" aria-label="Enable Revolut.me for paying">
                                    <input
                                        type="checkbox"
                                        checked={this.state.enabledPaymentMethodsForPaying.includes("REVOLUT_ME")}
                                        onChange={async (e) => {
                                            const enabled = this.state.enabledPaymentMethodsForPaying;
                                            const newEnabled: PaymentMethodType[] = e.target.checked
                                                ? [...enabled, "REVOLUT_ME"]
                                                : enabled.filter(m => m !== "REVOLUT_ME");
                                            // Update state immediately for responsive UI
                                            this.setState({
                                                enabledPaymentMethodsForPaying: newEnabled,
                                            });
                                            // Save to backend
                                            await this.handleUpdatePaymentMethodsForPaying(newEnabled);
                                        }}
                                        disabled={this.state.isLoading}
                                    />
                                    <span className="ProfilePage-ToggleSlider"></span>
                                </label>
                            </div>
                            <div className="ProfilePage-ToggleRow">
                                <div className="ProfilePage-ToggleText">
                                    <div className="ProfilePage-ToggleLabel">{i18nService.t('profile.paypalMe')}</div>
                                </div>
                                <label className="ProfilePage-ToggleSwitch" aria-label="Enable PayPal.me for paying">
                                    <input
                                        type="checkbox"
                                        checked={this.state.enabledPaymentMethodsForPaying.includes("PAYPAL_ME")}
                                        onChange={async (e) => {
                                            const enabled = this.state.enabledPaymentMethodsForPaying;
                                            const newEnabled: PaymentMethodType[] = e.target.checked
                                                ? [...enabled, "PAYPAL_ME"]
                                                : enabled.filter(m => m !== "PAYPAL_ME");
                                            // Update state immediately for responsive UI
                                            this.setState({
                                                enabledPaymentMethodsForPaying: newEnabled,
                                            });
                                            // Save to backend
                                            await this.handleUpdatePaymentMethodsForPaying(newEnabled);
                                        }}
                                        disabled={this.state.isLoading}
                                    />
                                    <span className="ProfilePage-ToggleSlider"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                <div className="ProfilePage-Section">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.changePassword')}</h2>
                    <p className="ProfilePage-SectionDescription">{i18nService.t('profile.passwordDescription')}</p>
                    <div className="ProfilePage-InputGroup">
                        <div className="ProfilePage-PasswordWrapper">
                            <input
                                type={this.state.showCurrentPassword ? "text" : "password"}
                                className="ProfilePage-Input ProfilePage-PasswordInput"
                                value={this.state.currentPassword}
                                onChange={e => this.setState({currentPassword: e.target.value, passwordError: undefined})}
                                placeholder={i18nService.t('profile.currentPassword')}
                                disabled={this.state.isLoading}
                            />
                            <button
                                type="button"
                                className={`ProfilePage-PasswordToggle ${this.state.showCurrentPassword ? "ProfilePage-PasswordToggle-visible" : ""}`}
                                onClick={this.toggleCurrentPasswordVisibility}
                                disabled={this.state.isLoading}
                                aria-label={this.state.showCurrentPassword ? "Hide password" : "Show password"}
                            >
                                <span className="ProfilePage-EyeIcon"></span>
                            </button>
                        </div>
                        <div className="ProfilePage-PasswordWrapper">
                            <input
                                type={this.state.showNewPassword ? "text" : "password"}
                                className="ProfilePage-Input ProfilePage-PasswordInput"
                                value={this.state.newPassword}
                                onChange={e => this.setState({newPassword: e.target.value, passwordError: undefined})}
                                placeholder={i18nService.t('profile.newPassword')}
                                disabled={this.state.isLoading}
                            />
                            <button
                                type="button"
                                className={`ProfilePage-PasswordToggle ${this.state.showNewPassword ? "ProfilePage-PasswordToggle-visible" : ""}`}
                                onClick={this.toggleNewPasswordVisibility}
                                disabled={this.state.isLoading}
                                aria-label={this.state.showNewPassword ? "Hide password" : "Show password"}
                            >
                                <span className="ProfilePage-EyeIcon"></span>
                            </button>
                        </div>
                        <div className="ProfilePage-PasswordWrapper">
                            <input
                                type={this.state.showConfirmPassword ? "text" : "password"}
                                className="ProfilePage-Input ProfilePage-PasswordInput"
                                value={this.state.confirmPassword}
                                onChange={e => this.setState({confirmPassword: e.target.value, passwordError: undefined})}
                                placeholder={i18nService.t('profile.confirmPassword')}
                                disabled={this.state.isLoading}
                            />
                            <button
                                type="button"
                                className={`ProfilePage-PasswordToggle ${this.state.showConfirmPassword ? "ProfilePage-PasswordToggle-visible" : ""}`}
                                onClick={this.toggleConfirmPasswordVisibility}
                                disabled={this.state.isLoading}
                                aria-label={this.state.showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                <span className="ProfilePage-EyeIcon"></span>
                            </button>
                        </div>
                        <button
                            className="ProfilePage-Button"
                            onClick={this.handleChangePassword}
                            disabled={this.state.isLoading || !this.state.currentPassword || !this.state.newPassword || !this.state.confirmPassword}
                        >
                            {this.state.isLoading ? i18nService.t('common.loading') : i18nService.t('profile.changePasswordButton')}
                        </button>
                    </div>
                    {this.state.passwordError && (
                        <div className="ProfilePage-Error">{this.state.passwordError}</div>
                    )}
                </div>

                <div className="ProfilePage-Section">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.exportData')}</h2>
                    <p className="ProfilePage-SectionDescription">{i18nService.t('profile.exportDescription')}</p>
                    <div className="ProfilePage-ButtonRow">
                        <button
                            className="ProfilePage-Button"
                            onClick={this.handleExportMyData}
                            disabled={this.state.isLoading}
                        >
                            {this.state.isLoading ? i18nService.t('common.loading') : i18nService.t('profile.downloadData')}
                        </button>
                    </div>
                </div>

                <div className="ProfilePage-Section ProfilePage-SectionDanger">
                    <h2 className="ProfilePage-SectionTitle">{i18nService.t('profile.deleteAccount')}</h2>
                    <p className="ProfilePage-SectionDescription">
                        {i18nService.t('profile.deleteDescription')}
                    </p>
                    <div className="ProfilePage-ButtonRow">
                        <button
                            className="ProfilePage-Button ProfilePage-ButtonDanger"
                            onClick={this.handleDeleteAccount}
                            disabled={this.state.isLoading}
                        >
                            {this.state.isLoading ? i18nService.t('common.loading') : i18nService.t('profile.deleteButton')}
                        </button>
                    </div>
                </div>
                </div>

        </div>
    }
}

export default withRouter(ProfilePage);

